import { observer } from 'mobx-react-lite'
import { Outlet } from 'react-router'
import { type ITooltipProps, Button, Tabs, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { ManageSeatsStore } from 'entities/Seat/ui/ManageSeats/store/ManageSeatsStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { noManageBillingPermissionTooltipLabel } from 'entities/UsersPermissions'
import { useMembersContext } from 'pages/settings/pages/members/context/membersContext'
import {
  MembersViewProvider,
  useMembersViewContext,
} from 'pages/settings/pages/members/pages/membersView/context/membersViewContext'
import { MembersPath } from 'pages/settings/pages/members/route/membersPath'
import { getMemberGracePeriodTooltipProps } from 'pages/settings/pages/members/lib/memberTooltipProps'
import styles from './styles.module.scss'

export const MembersViewContent = observer(() => {
  const { manageMemberWrapper } = useMembersContext()
  const membersViewStore = useMembersViewContext()
  const { tabs, seatsUsed, seatsTotal } = membersViewStore
  const entities = useEntitiesContext()

  const handleManageSeats = () => {
    manageMemberWrapper(() => new ManageSeatsStore(entities).openModal())
  }

  const handleAddMembers = () => {
    manageMemberWrapper(() => {
      uiStore.changeRoute({
        path: MembersPath.add,
      })
    })
  }

  const isBillingAccess = entities.userPermissionsStore.getItem('can_manage_billing_settings')
  const isGracePeriod = entities.organizationStore.isNumbersGracePeriod
  const isAddMembersDisabled = isGracePeriod || !isBillingAccess

  const getAddMembersTooltipProps = (): ITooltipProps | undefined => {
    if (isGracePeriod) return getMemberGracePeriodTooltipProps()
    if (!isBillingAccess)
      return {
        label: noManageBillingPermissionTooltipLabel,
        placement: 'top',
        breakWord: true,
      }

    return undefined
  }

  return (
    <div className={styles.content}>
      <div className={styles.membersHeader}>
        <div className={styles.headerDetails}>
          <Typography variant='heading-lg' ariaLabel='Members_title'>
            Members
          </Typography>
          <Typography variant='body-md-regular' ariaLabel='Seats_subtitle_text' color='tertiary'>
            Seats used:&nbsp;
            <Typography color='var(--content-primary-primary)' ariaLabel='SeatsUsed'>
              {seatsUsed}
            </Typography>
            &nbsp;/&nbsp;
            <Typography color='var(--content-primary-primary)' ariaLabel='SeatsTotal'>
              {seatsTotal}
            </Typography>
          </Typography>
        </div>
        <div className={styles.headerActions}>
          {isBillingAccess && (
            <Button
              text='Manage seats'
              contained='secondary'
              iconProps={{ icon: 'checkVerified', color: 'tertiary' }}
              size='medium'
              onClick={handleManageSeats}
            />
          )}
          <Button
            text='Add members'
            iconProps={{ icon: 'add' }}
            size='medium'
            onClick={handleAddMembers}
            disabled={isAddMembersDisabled}
            tooltipProps={getAddMembersTooltipProps()}
          />
        </div>
      </div>
      <div className={styles.navBar}>
        <Tabs tabs={tabs} isNavigate tabsClassName={styles.tabs} />
        <Outlet />
      </div>
    </div>
  )
})

export const MembersView = () => {
  return (
    <MembersViewProvider>
      <MembersViewContent />
    </MembersViewProvider>
  )
}
